import { useState, useEffect, useCallback } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function fmt(value, currency = "EUR") {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(value);
  } catch { return `${value} ${currency}`; }
}

export function CompareDialog({ invoiceId, onClose, onChanged, baseCurrency, lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/invoices/${invoiceId}/albaranes/compare`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const applyAlbaranPrice = async (line) => {
    setApplying(line.invoice_line_index);
    try {
      await api.put(
        `/invoices/${invoiceId}/line-items/${line.invoice_line_index}/apply-albaran-price`,
        { unit_price: line.albaran.unit_price }
      );
      toast.success(lang === "es" ? "Factura corregida" : "Invoice updated");
      onChanged?.();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(null);
    }
  };

  const openClaim = async () => {
    try {
      const r = await api.get(`/invoices/${invoiceId}/albaranes/claim.html`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke later (after the new tab loaded the resource)
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !data) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{lang === "es" ? "Cargando…" : "Loading…"}</DialogTitle></DialogHeader>
          <p className="text-center py-10 text-[var(--text-muted)]">…</p>
        </DialogContent>
      </Dialog>
    );
  }

  const { summary, lines, only_on_albaran: onlyAlb } = data;
  const cur = data.invoice.currency || baseCurrency;
  const totalSign = summary.total_diff > 0 ? "+" : summary.total_diff < 0 ? "−" : "";
  const totalColor = summary.total_diff > 0
    ? "text-[var(--status-negative)]"
    : summary.total_diff < 0
    ? "text-[var(--status-positive)]"
    : "text-[var(--text-muted)]";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle>
            {lang === "es" ? "Comparar factura ↔ albaranes" : "Compare invoice ↔ delivery notes"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="px-3 py-2 rounded border border-[var(--border-color)] bg-[var(--surface)]">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Factura" : "Invoice"}
            </div>
            <div className="font-mono-num font-semibold">{fmt(summary.invoice_total, cur)}</div>
          </div>
          <div className="px-3 py-2 rounded border border-[var(--border-color)] bg-[var(--surface)]">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? `Albaranes (${summary.albaranes_count})` : `Notes (${summary.albaranes_count})`}
            </div>
            <div className="font-mono-num font-semibold">{fmt(summary.albaranes_total, cur)}</div>
          </div>
          <div className={`px-3 py-2 rounded border border-[var(--border-color)] bg-[var(--surface)] ${totalColor}`}>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Diferencia" : "Diff"}
            </div>
            <div className="font-mono-num font-semibold" data-testid="compare-total-diff">
              {totalSign}{fmt(Math.abs(summary.total_diff), cur)}
              {summary.total_pct != null && (
                <span className="text-xs opacity-70 ml-1">({summary.total_pct}%)</span>
              )}
            </div>
          </div>
          <div className="px-3 py-2 rounded border border-[var(--border-color)] bg-[var(--surface)]">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Discrepancias" : "Mismatches"}
            </div>
            <div className="font-mono-num font-semibold">{summary.mismatched_lines}</div>
            <div className="text-[10px] text-[var(--text-muted)]">
              {summary.missing_on_invoice} {lang === "es" ? "solo en albarán" : "only in note"} · {summary.missing_on_albaran} {lang === "es" ? "solo en factura" : "only in invoice"}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compare-lines-table">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              <tr className="border-b border-[var(--border-color)]">
                <th className="text-left py-2 px-2">{lang === "es" ? "Producto" : "Product"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "Qty fac." : "Inv qty"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "Qty alb." : "Note qty"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "€/ud fac." : "Inv €"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "€/ud alb." : "Note €"}</th>
                <th className="text-right py-2 px-2">Δ €</th>
                <th className="text-right py-2 px-2">Δ %</th>
                <th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const diffColor = (l.unit_price_diff || 0) > 0 ? "text-[var(--status-negative)]"
                                : (l.unit_price_diff || 0) < 0 ? "text-[var(--status-positive)]"
                                : "";
                return (
                  <tr key={l.invoice_line_index}
                      className="border-b border-[var(--border-color)] last:border-0"
                      data-testid={`compare-line-${l.invoice_line_index}`}>
                    <td className="py-1.5 px-2 capitalize">{l.name}</td>
                    <td className="py-1.5 px-2 text-right font-mono-num">{l.invoice.qty}</td>
                    <td className="py-1.5 px-2 text-right font-mono-num">{l.albaran ? l.albaran.qty : "—"}</td>
                    <td className="py-1.5 px-2 text-right font-mono-num">{fmt(l.invoice.unit_price, cur)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-num">{l.albaran && l.albaran.unit_price != null ? fmt(l.albaran.unit_price, cur) : "—"}</td>
                    <td className={`py-1.5 px-2 text-right font-mono-num ${diffColor}`}>
                      {l.unit_price_diff != null ? (l.unit_price_diff > 0 ? "+" : "") + l.unit_price_diff.toFixed(2) : "—"}
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono-num ${diffColor}`}>
                      {l.unit_price_pct != null ? `${l.unit_price_pct > 0 ? "+" : ""}${l.unit_price_pct}%` : "—"}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      {l.match && (l.unit_price_diff || 0) !== 0 && l.albaran && l.albaran.unit_price != null && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => applyAlbaranPrice(l)}
                          disabled={applying === l.invoice_line_index}
                          data-testid={`apply-albaran-price-${l.invoice_line_index}`}
                        >
                          {applying === l.invoice_line_index
                            ? "…"
                            : (lang === "es" ? "Usar alb." : "Use note")}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {onlyAlb.length > 0 && (
                <>
                  <tr><td colSpan={8} className="pt-4 pb-1 text-[10px] uppercase tracking-wider text-amber-500">
                    {lang === "es" ? "Solo en albaranes (no facturados)" : "Only on delivery notes"}
                  </td></tr>
                  {onlyAlb.map((o) => (
                    <tr key={`oa-${o.name}`} className="border-b border-[var(--border-color)] last:border-0 text-amber-500">
                      <td className="py-1.5 px-2 capitalize">{o.name}</td>
                      <td className="py-1.5 px-2 text-right font-mono-num">—</td>
                      <td className="py-1.5 px-2 text-right font-mono-num">{o.qty}</td>
                      <td className="py-1.5 px-2 text-right font-mono-num">—</td>
                      <td className="py-1.5 px-2 text-right font-mono-num">{fmt(o.unit_price, cur)}</td>
                      <td colSpan={3} />
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={openClaim}
            data-testid="open-claim-report"
          >
            <FileText size={14} className="mr-1.5" />
            {lang === "es" ? "Generar reclamación" : "Claim report"}
          </Button>
          <Button variant="outline" onClick={onClose}>{lang === "es" ? "Cerrar" : "Close"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
